<template>
  <v-app
    v-if="slug && themeLoaded"
    class="default"
    :class="`${layout} ${heroClass}`"
    :style="{
      '--hero-image-url': 'url(' + heroImagePath + ')'
    }"
    @drop.stop.prevent
  >
    <link
      rel="preload"
      href="@/assets/fonts/roboto-v29-latin/roboto-v30-latin-regular.woff2"
      as="font"
      type="font/woff2"
      crossorigin
    />
    <link
      rel="preload"
      href="@/assets/fonts/materialdesignicons-webfont.woff2"
      as="font"
      type="font/woff2"
      crossorigin
    />

    <v-app-bar v-if="!this.$route.meta.noHeader" app>
      <v-container>
        <v-row>
          <v-col cols="5" md="3" id="brand-logo">
            <a
              href="#main-content"
              tab-index="0"
              class="skip-link"
              id="skip-link"
            >
              Zum Inhalt springen
            </a>
            <router-link v-if="!swapHomePageButtons" :to="homeRoute">
              <img
                class="app-bar__company-logo"
                :src="`${apiUrl}/file/public?slug=${slug}&fileName=${companyLogoName}`"
                :alt="localize(companyLogoAltText)"
              />
            </router-link>
            <a v-else :href="companyLogoUrl" target="_blank">
              <img
                class="app-bar__company-logo"
                :src="`${apiUrl}/file/public?slug=${slug}&fileName=${companyLogoName}`"
                :alt="localize(companyLogoAltText)"
              />
            </a>
          </v-col>
          <v-col cols="12" md="4" class="d-none d-md-flex">
            <template v-if="showAppBarSearch">
              <fulltext-search filled></fulltext-search>
            </template>
          </v-col>
          <v-col
            cols="7"
            md="5"
            class="ml-auto d-flex justify-end align-center"
          >
            <template v-if="companyLogoUrl && !swapHomePageButtons">
              <v-btn
                :href="companyLogoUrl"
                target="_blank"
                text
                class="ml-auto px-4 px-sm-8 mr-0 mr-sm-4"
              >
                <v-icon class="mr-0 mr-sm-2">mdi-home</v-icon>
                <span class="d-none d-sm-inline">{{
                  localize(homepageLabel)
                }}</span>
              </v-btn>
            </template>
            <template
              v-else-if="swapHomePageButtons && localize(homepageLabel)"
            >
              <v-btn
                text
                @click="goTo(homeRoute)"
                class="ml-auto px-4 px-sm-8 mr-0 mr-sm-4"
              >
                <v-icon class="mr-0 mr-sm-2">mdi-home</v-icon>
                <span class="d-none d-sm-inline">{{
                  localize(homepageLabel)
                }}</span>
              </v-btn>
            </template>

            <v-chip v-if="userIsInternal" pill color="primary">
              Interne Jobs
              <v-btn small icon color="white" @click="setExternal">
                <v-icon small>mdi-power</v-icon>
              </v-btn>
            </v-chip>

            <language-menu></language-menu>
            <account-menu></account-menu>
          </v-col>
        </v-row>
      </v-container>
    </v-app-bar>

    <div
      v-if="this.$route.meta.hasScrollMenu"
      class="d-flex"
      style="justify-content: center"
    >
      <v-container class="fixed-menu d-none" id="fixed-top-menu">
        <v-banner dense elevation="3" class="white">
          <router-view name="scrollMenu"></router-view>
        </v-banner>
      </v-container>
    </div>

    <component :is="layout" :heroClaim="heroClaim" :slug="slug"></component>

    <footer v-if="!this.$route.meta.noFooter">
      <html-content
        v-if="footerContent?.text && footerContent.text.length > 0"
        :data="footerContent"
      ></html-content>

      <div class="bottom-footer">
        <v-container class="d-block d-md-flex text-center text-md-left">
          <div class="flex-grow-1 mb-8 mb-md-0">
            &copy; {{ currentYear }} - {{ copyrightInfo }}
          </div>
          <nav class="ml-auto" :aria-label="$t('footerLegal')">
            <ul class="footer-links">
              <li class="footer-links__item">
                <router-link v-if="!customImprintUrl" :to="{ name: 'imprint' }">
                  <span class="text-sm text-md-reg">{{ $t('imprint') }}</span>
                </router-link>

                <a v-else :href="customImprintUrl" target="_blank">
                  <span class="text-sm text-md-reg">{{ $t('imprint') }}</span>
                </a>
              </li>

              <li class="footer-links__item">
                <router-link
                  v-if="!customDataPrivacyUrl"
                  :to="{ name: 'privacyPolicy' }"
                >
                  <span class="text-sm text-md-reg">
                    {{ $t('privacyPolicy') }}
                  </span>
                </router-link>

                <a v-else :href="customDataPrivacyUrl" target="_blank">
                  <span class="text-sm text-md-reg">
                    {{ $t('privacyPolicy') }}
                  </span>
                </a>
              </li>

              <li
                v-for="(item, index) in additionalFooterLinks"
                :key="`footer_link_${index}`"
                class="footer-links__item"
              >
                <router-link
                  v-if="item.route"
                  :to="{
                    name: item.route.name,
                    params: {
                      slug: 'roland',
                      siteName: 'accessibility'
                    }
                  }"
                  :aria-label="localize(item.label)"
                >
                  <span class="text-sm text-md-reg">
                    {{ localize(item.label) }}
                  </span>
                </router-link>

                <a
                  v-else-if="item.href"
                  :href="item.href"
                  :target="item.target"
                  :aria-label="localize(item.label)"
                >
                  <span class="text-sm text-md-reg">
                    {{ localize(item.label) }}
                  </span>
                </a>

                <a
                  v-else-if="item.onclick"
                  :onclick="item.onclick + ';return false;'"
                  :aria-label="localize(item.label)"
                  href="#"
                  role="button"
                >
                  <span class="text-sm text-md-reg">
                    {{ localize(item.label) }}
                  </span>
                </a>
              </li>
            </ul>
          </nav>
        </v-container>
      </div>
    </footer>

    <v-snackbar
      v-model="jobalertSuccess"
      :timeout="snackbarTimeout"
      color="success"
      role="status"
      aria-atomic="true"
    >
      {{ $t('jobalert.successMessage') }}

      <template v-slot:action="{ attrs }">
        <v-btn
          fab
          text
          v-bind="attrs"
          @click="jobalertSuccess = false"
          :title="$t('close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar
      v-model="jobalertError"
      color="red"
      :timeout="snackbarTimeout"
      role="alert"
      aria-atomic="true"
    >
      {{ $t('jobalert.errorMessage') }}

      <template v-slot:action="{ attrs }">
        <v-btn
          fab
          text
          v-bind="attrs"
          @click="jobalertError = false"
          :title="$t('close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar
      v-model="showSuccessMessage"
      :timeout="snackbarTimeout"
      color="success"
      role="status"
      aria-atomic="true"
    >
      {{ successMessage }}

      <template v-slot:action="{ attrs }">
        <v-btn
          fab
          text
          v-bind="attrs"
          @click="showSuccessMessage = false"
          :title="$t('close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar
      v-model="showErrorMessage"
      color="red"
      :timeout="snackbarTimeout"
      role="alert"
      aria-atomic="true"
    >
      {{ errorMessage }}

      <template v-slot:action="{ attrs }">
        <v-btn
          fab
          text
          v-bind="attrs"
          @click="showErrorMessage = false"
          :title="$t('close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <template v-if="useConsentManager">
      <comply-consent-manager :apiKey="consentManagerKey" />
    </template>
    <template v-else-if="slug">
      <cookie-banner />
    </template>

    <content-dialog></content-dialog>

    <v-snackbar
      v-model="applicationDisabledMode"
      color="red"
      bottom
      timeout="-1"
    >
      {{ $t('applicationDisabledModeInfo') }}
    </v-snackbar>

    <register-dialog v-model="showRegisterDialog" />
  </v-app>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { eventBus } from '@/event-bus.js';

import languageMenu from '@/components/language-menu.vue';
import accountMenu from '@/components/account-menu.vue';
import registerDialog from '@/components/application/register-dialog.vue';
import fulltextSearch from '@/components/fulltext-search.vue';
import cookieBanner from '@/components/cookie-banner.vue';
import htmlContent from '@/components/content/html-content.vue';
import contentDialog from '@/components/content-dialog.vue';

import { extend } from 'vee-validate';
import { required, email } from 'vee-validate/dist/rules';
import { loadUsercentrics } from '@/plugins/usercentrics.js';
import { SESSION_KEYS } from '@/constants.js';
import { updateNoIndexTag } from '@/helpers/NoIndexHelper.js';
import TrackingService from '@/services/TrackingService.js';
import { captureUtm } from '@/helpers/UtmHelper.js';
import { initializeReferrerAttribution } from '@/helpers/ReferrerHelper.js';

export default {
  name: 'App',

  data: () => ({
    themeLoaded: false,
    slug: null,
    logoName: null,
    logoAltText: [],
    webguardKey: process.env.VUE_APP_WEBGUARD_KEY,
    consentManagerKey: process.env.VUE_APP_CONSENT_MANAGER_KEY,
    companyLayout: 'top-filter-layout',
    jobalertError: false,
    jobalertSuccess: false,
    snackbarTimeout: 5000,
    errorMessage: '',
    showErrorMessage: false,
    successMessage: '',
    showSuccessMessage: false,
    showRegisterDialog: false,
    homepageUrl: '',
    homepageLabel: [],
    copyrightLabel: [],
    customImprintUrl: '',
    customDataPrivacyUrl: '',
    customCss: '',
    customLogoRouteNames: ['jobDetails', 'apply'],
    anchorMenuInitialized: false
  }),

  created() {
    if (this.useConsentManager) {
      let tag = document.createElement('script');
      tag.setAttribute('src', 'https://webguard.cb-sol.de/loader.js');
      tag.setAttribute('apiKey', this.webguardKey);
      document.head.appendChild(tag);

      tag = document.createElement('script');
      tag.setAttribute('src', 'https://ccl-webguard.cb-sol.de/js/app.js');
      document.head.appendChild(tag);
    }
  },

  async mounted() {
    const response = await this.getCompanySettings();
    const settings = await response.json();

    this.slug = settings.slug;
    this.setCompanySettings(settings);
    updateNoIndexTag(this.$route);
    this.setReferrer(initializeReferrerAttribution());

    // Capture first-touch UTM values and current-tab Jobalert attribution.
    captureUtm();

    const storageKey = `languageKey_${settings.slug}`;
    const queryLanguageKey = this.$route.query.lang?.substring(0, 2);
    if (queryLanguageKey && this.languages[queryLanguageKey]) {
      window.localStorage.setItem(storageKey, queryLanguageKey);
    }

    const storedLanguageKey = window.localStorage.getItem(storageKey);
    if (storedLanguageKey && this.languages[storedLanguageKey]) {
      this.setCurrentLanguage(this.languages[storedLanguageKey]);
      this.$i18n.locale = storedLanguageKey;
    } else {
      window.localStorage.setItem(
        storageKey,
        this.currentLanguage.substring(0, 2)
      );
    }

    if (settings.pageTitle && settings.pageTitle.length > 0) {
      document.title = this.localize(settings.pageTitle);
    }

    if (settings.pageDescription && settings.pageDescription.length > 0) {
      let meta = document.querySelector("meta[name='description']");
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'description';
        document.getElementsByTagName('head')[0].appendChild(meta);
      }
      meta.content = this.localize(settings.pageDescription);
    }

    if (settings.defaultClaim && settings.defaultClaim.length > 0) {
      this.setHeroClaim(settings.defaultClaim);
      this.setDefaultHeroClaim(settings.defaultClaim);
    }

    if (settings.defaultHeroImage) {
      this.setHeroImage(settings.defaultHeroImage);
      this.setDefaultHeroImage(settings.defaultHeroImage);
    }

    await this.setCompanyLocale(settings.slug);

    this.logoName = settings.companyLogoName;
    this.logoAltText = settings.companyLogoAltText;
    this.companyLayout = settings.layout;
    this.homepageUrl = settings.homepageUrl;
    this.homepageLabel = settings.homepageLabel;
    this.copyrightLabel = settings.copyrightLabel;
    this.customImprintUrl = settings.customImprintUrl;
    this.customDataPrivacyUrl = settings.customDataPrivacyUrl;
    this.swapHomePageButtons = settings.swapHomePageButtons;
    this.customCss = settings.customCss;

    this.setRouteImages(settings.routeImages);

    this.loadDepartments(settings.slug);
    this.loadLocations(settings.slug);

    if (this.$route.params.internalToken || this.userIsInternal) {
      try {
        let token = this.$route.params.internalToken;
        if (!token) {
          token = localStorage.getItem(SESSION_KEYS.internalToken);
        }

        await this.loadInternalJobs({
          slug: settings.slug,
          internalToken: token
        });

        this.setInternal(token);
      } catch {
        this.setUserExternal();
        await this.loadJobs(settings.slug);
      }
    } else {
      this.setUserExternal();
      await this.loadJobs(settings.slug);
    }

    this.setFavicon();

    this.setCustomCss(settings.customCss);

    this.themeLoaded = true;
    this.setupCustomScripts(settings);

    this.setupEventBus();
    this.setupValidationRules();

    // Surface the confirmation after a profile deletion. The candidate is logged
    // out and redirected back here with this flag, so the message is shown once
    // the app reloads. Strip the flag so a refresh doesn't repeat it.
    if (this.$route.query.accountDeleted) {
      eventBus.$emit('successMessage', this.$t('cockpit.profileDeleted'));

      const query = { ...this.$route.query };
      delete query.accountDeleted;
      this.$router.replace({ query }).catch(() => {});
    }

    this.setupAnchorMenu();
    this.$nextTick(() => {
      this.flushPageViewIfReady();
    });
  },

  computed: {
    ...mapState({
      heroImageUrl: 'heroImageUrl',
      routeImages: 'routeImages',
      currentLanguage: 'currentLanguage'
    }),

    ...mapState('companyModule', {
      additionalFooterLinks: 'additionalFooterLinks',
      currentCompanyId: 'currentCompanyId',
      customChildCompanyLogos: 'customChildCompanyLogos',
      customChildCompanyLogoAltText: 'customChildCompanyLogoAltText',
      customChildCompanyLogoUrl: 'customChildCompanyLogoUrl',
      jobportalOnly: 'jobportalOnly',
      footerContent: 'footerContent',
      pageTitle: 'pageTitle',
      languages: 'languages'
    }),

    ...mapGetters('companyModule', {
      apiUrl: 'currentCompanyApiUrl'
    }),

    ...mapGetters({
      heroClaim: 'heroClaim',
      userIsInternal: 'companyModule/userIsInternal'
    }),

    applicationDisabledMode() {
      return process.env.VUE_APP_DISABLE_APPLY === 'true';
    },

    companyLogoName() {
      if (this.customLogoRouteNames.includes(this.$route.name)) {
        if (
          this.customChildCompanyLogos &&
          Object.hasOwn(this.customChildCompanyLogos, this.currentCompanyId)
        ) {
          return this.customChildCompanyLogos[this.currentCompanyId];
        }
      }

      return this.logoName;
    },

    companyLogoAltText() {
      if (this.customLogoRouteNames.includes(this.$route.name)) {
        if (
          this.customChildCompanyLogoAltText &&
          Object.hasOwn(
            this.customChildCompanyLogoAltText,
            this.currentCompanyId
          )
        ) {
          return this.customChildCompanyLogoAltText[this.currentCompanyId];
        }
      }

      return this.logoAltText;
    },

    companyLogoUrl() {
      if (this.customLogoRouteNames.includes(this.$route.name)) {
        if (
          this.customChildCompanyLogoUrl &&
          Object.hasOwn(this.customChildCompanyLogoUrl, this.currentCompanyId)
        ) {
          return this.customChildCompanyLogoUrl[this.currentCompanyId];
        }
      }

      return this.homepageUrl;
    },

    copyrightInfo() {
      const localizedLabel = this.localize(this.copyrightLabel);
      if (localizedLabel) {
        return localizedLabel;
      } else {
        return 'hidden professionals GmbH';
      }
    },

    currentYear() {
      return new Date().getFullYear();
    },

    heroClass() {
      let heroClass = '';
      if (this.$route.meta.heroClass) {
        heroClass = this.$route.meta.heroClass;
      }

      return heroClass;
    },

    heroImagePath() {
      let imgUrl = '';

      if (this.heroImageUrl) {
        imgUrl = `${this.apiUrl}/file/public?slug=${this.slug}&fileName=${this.heroImageUrl}`;
      }

      return imgUrl;
    },

    layout() {
      return this.$route.meta.layout || this.companyLayout;
    },

    homeRoute() {
      if (this.jobportalOnly) {
        return { name: 'jobs' };
      } else {
        return `/${this.slug}`;
      }
    },

    showAppBarSearch() {
      return this.layout === 'top-filter-layout';
    },

    // Webguard/Comply is only used for Biotest, so no generic consent bridge is needed here.
    useConsentManager() {
      return this.slug && this.slug === 'biotest';
    }
  },

  watch: {
    $route() {
      this.setCompanyId(-1);
      this.anchorMenuInitialized = false;
      this.setupAnchorMenu();

      if (
        !this.$route.meta.customPageTitle &&
        this.pageTitle &&
        this.pageTitle.length > 0
      ) {
        document.title = this.localize(this.pageTitle);
      }

      this.$nextTick(() => {
        this.flushPageViewIfReady();
      });

      setTimeout(() => {
        // The brand logo link is only present once the app shell has rendered
        // (slug + theme loaded); guard against it being absent on some routes.
        const brandLogoLink = document.querySelector('#brand-logo a');
        if (brandLogoLink) {
          brandLogoLink.focus();
        }
      }, 50);
    },

    currentLanguage() {
      this.setupValidationRules();
    }
  },

  methods: {
    ...mapActions('companyModule', {
      getCompanySettings: 'getCompanySettings',
      loadDepartments: 'loadDepartments',
      loadLocations: 'loadLocations',
      loadLocale: 'loadLocale'
    }),

    ...mapActions('jobsModule', {
      loadJobs: 'loadJobs',
      loadInternalJobs: 'loadInternalJobs'
    }),

    ...mapMutations({
      setBenefits: 'companyModule/setBenefits',
      setCompanySettings: 'companyModule/setCompanySettings',
      setCompanySlug: 'companyModule/setCompanySlug',
      setCompanyId: 'companyModule/setCurrentCompanyId',
      setReferrer: 'companyModule/setReferrer',
      setInternal: 'companyModule/setInternal',
      setUserExternal: 'companyModule/setExternal',
      setHeroClaim: 'setHeroClaim',
      setDefaultHeroClaim: 'setDefaultHeroClaim',
      setHeroImage: 'setHeroImage',
      setDefaultHeroImage: 'setDefaultHeroImage',
      setRouteImages: 'setRouteImages',
      setCurrentLanguage: 'setCurrentLanguage'
    }),

    goTo(route) {
      this.$router.push(route);
    },

    flushPageViewIfReady() {
      if (this.$route.meta.customPageTitle) {
        return;
      }

      TrackingService.flushPageView();
    },

    isObject(item) {
      return item && typeof item === 'object' && !Array.isArray(item);
    },

    async loadCompanyLocale(slug, messages) {
      const companyMessages = await this.loadLocale(slug);

      this.mergeDeep(messages, companyMessages);

      this.$i18n.setLocaleMessage('de', messages['de']);
      this.$i18n.setLocaleMessage('en', messages['en']);
    },

    mergeDeep(target, ...sources) {
      if (!sources.length) return target;
      const source = sources.shift();

      if (this.isObject(target) && this.isObject(source)) {
        for (const key in source) {
          if (this.isObject(source[key])) {
            if (!target[key]) Object.assign(target, { [key]: {} });
            this.mergeDeep(target[key], source[key]);
          } else {
            Object.assign(target, { [key]: source[key] });
          }
        }
      }

      return this.mergeDeep(target, ...sources);
    },

    observerCallback(entries) {
      entries.forEach((entry) => {
        const menuElement = document.getElementById('fixed-top-menu');
        if (!menuElement) {
          return;
        }

        if (entry.isIntersecting) {
          menuElement.classList.add('d-none');
        } else {
          menuElement.classList.remove('d-none');
        }
      });
    },

    async setCompanyLocale(slug) {
      const locales = require.context(
        './locales',
        true,
        /[A-Za-z0-9-_,\s]+\.json$/i
      );

      const messages = {};
      locales.keys().forEach((key) => {
        const matched = key.match(/([A-Za-z0-9-_]+)\./i);
        if (matched && matched.length > 1) {
          const locale = matched[1];
          messages[locale] = locales(key);
        }
      });

      await this.loadCompanyLocale(slug, messages);
    },

    setCustomCss(customCss) {
      if (!customCss) {
        return;
      }

      const style = document.createElement('style');
      style.innerHTML = customCss;
      document.head.appendChild(style);
    },

    setExternal() {
      this.setUserExternal();
      window.location.reload();
    },

    setFavicon() {
      const favicon = document.getElementById('favicon');
      if (favicon.href === window.location.href) {
        favicon.href = `${this.apiUrl}/file/public?slug=${this.slug}&fileName=favicon.ico`;

        const faviconPng = document.getElementById('faviconPng');
        faviconPng.href = `${this.apiUrl}/file/public?slug=${this.slug}&fileName=favicon-32x32.png`;

        const faviconSvg = document.getElementById('faviconSvg');
        faviconSvg.href = `${this.apiUrl}/file/public?slug=${this.slug}&fileName=favicon.svg`;

        const touchIcon = document.getElementById('touchIcon');
        touchIcon.href = `${this.apiUrl}/file/public?slug=${this.slug}&fileName=android-chrome-192x192.png`;
      }
    },

    setupAnchorMenu() {
      if (this.anchorMenuInitialized) {
        return;
      }

      this.anchorMenuInitialized = true;
      window.setTimeout(() => {
        const elementTargets =
          document.getElementsByClassName('fixed-menu-anchor');
        if (!elementTargets || elementTargets.length === 0) {
          return;
        }

        const elementTarget = elementTargets[0];

        let observerOptions = {
          rootMargin: '-80px',
          threshold: 0.5
        };

        const observer = new IntersectionObserver(
          this.observerCallback,
          observerOptions
        );

        observer.observe(elementTarget);
      }, 800);
    },

    async setupCustomScripts(settings) {
      if (settings.customScripts && settings.customScripts.length > 0) {
        for (const script of settings.customScripts) {
          if (script.indexOf('<script') === 0) {
            const scriptWrapper = document.createElement('div');
            scriptWrapper.innerHTML = script;
            document.head.appendChild(scriptWrapper.firstChild);
          } else if (script.indexOf('usercentrics:') === 0) {
            const token = script.replace('usercentrics:', '');
            const uc = await loadUsercentrics(token);
            await uc.ready;
          }
        }
      }
    },

    setupEventBus() {
      eventBus.$on('jobalert-success', () => {
        this.jobalertSuccess = true;
      });

      eventBus.$on('jobalert-error', () => {
        this.jobalertError = true;
      });

      eventBus.$on('successMessage', (message) => {
        this.showSuccessMessage = true;
        this.successMessage = message;
      });

      eventBus.$on('errorMessage', (message) => {
        this.showErrorMessage = true;
        this.errorMessage = message;
      });

      eventBus.$on('openRegister', () => {
        this.showRegisterDialog = true;
      });
    },

    setupValidationRules() {
      extend('required', {
        ...required,
        message: this.$t('validation.fieldIsMandatory')
      });

      extend('email', {
        ...email,
        message: this.$t('validation.emailInvalid')
      });

      extend('fileUploaded', {
        validate(value) {
          return value && value.length > 0;
        },

        message: this.$t('validation.fileIsMandatory')
      });

      extend('isTrue', {
        validate(value) {
          return value;
        },

        message: this.$t('validation.fieldIsMandatory')
      });
    }
  },

  components: {
    cookieBanner,
    languageMenu,
    accountMenu,
    registerDialog,
    fulltextSearch,
    htmlContent,
    contentDialog
  }
};

import('@/assets/styles/materialdesignicons.min.css');
import('@/assets/styles/base/base.scss');
</script>

<style lang="scss" scoped>
.hero {
  .v-banner {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;

    .v-banner__wrapper {
      padding: 2rem;
    }
  }
}
</style>
